import React, { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import AdVideoPlaybackModal from './AdVideoPlaybackModal';
import { normalizeAdMediaUrl } from '../../utils/adMediaUrl';
import {
  canPlayInAppVideo,
  getAdLibraryUrl,
  imageReferrerPolicy,
  isVideoAd,
  shouldShowPlayButton,
} from '../../utils/adVideoPlayback';
import './AdCreativeThumbnail.css';

export default function AdCreativeThumbnail({
  ad,
  playbackEnabled = true,
  imageClassName = '',
  fallbackClassName = '',
  imgStyle,
  eager = false,
  mountKey = '',
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imgAttempt, setImgAttempt] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const variant = ad?.imageVariant || ad?.platform || 'linkedin';
  const mediaSrc = normalizeAdMediaUrl(ad?.mediaUrl);
  const showPlayControl = shouldShowPlayButton(ad);
  const inAppPlay = canPlayInAppVideo(ad, playbackEnabled);
  const libraryUrl = getAdLibraryUrl(ad);
  const videoAd = isVideoAd(ad);
  const showImage = Boolean(mediaSrc) && !imageFailed;
  const useNexusFallback = Boolean(fallbackClassName?.includes('nexus-ad-tile__image'));
  const showGradientFallback = !showImage && (useNexusFallback || videoAd);
  const imgCacheKey = `${mountKey}:${mediaSrc || ''}:${imgAttempt}`;

  useEffect(() => {
    setImageFailed(false);
    setImageLoaded(false);
    setImgAttempt(0);
  }, [mediaSrc, ad?.videoPlaybackUrl, mountKey]);

  const handlePlayClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (inAppPlay) {
      setModalOpen(true);
      return;
    }
    if (libraryUrl) {
      window.open(libraryUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const wrapperClass = [
    'ad-creative-thumb',
    showImage ? 'ad-creative-thumb--remote' : 'ad-creative-thumb--fallback',
    showImage ? imageClassName : (fallbackClassName || `ad-creative-thumb__placeholder ad-creative-thumb__placeholder--${variant}`),
  ].filter(Boolean).join(' ');

  const referrerPolicy = imageReferrerPolicy(ad);

  const playButton = showPlayControl ? (
    <button
      type="button"
      className="ad-creative-thumb__play nodrag"
      onClick={handlePlayClick}
      aria-label={
        inAppPlay
          ? `Play video: ${ad?.title || ad?.headline || 'Ad'}`
          : `View video ad in library: ${ad?.title || ad?.headline || 'Ad'}`
      }
      title={inAppPlay ? 'Play video' : 'View in Ad Library'}
    >
      <span className="ad-creative-thumb__play-icon" aria-hidden>
        <Play size={26} strokeWidth={2.5} />
      </span>
    </button>
  ) : null;

  const videoBadge = videoAd && !showPlayControl ? (
    <span className="ad-creative-thumb__badge" aria-hidden>Video</span>
  ) : null;

  const showSpinner = showImage && !imageLoaded;

  return (
    <>
      <div className={wrapperClass}>
        {showImage ? (
          <img
            key={imgCacheKey}
            src={mediaSrc}
            alt={ad.title || ad.headline || 'Ad creative'}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            {...(referrerPolicy ? { referrerPolicy } : {})}
            style={imgStyle}
            ref={(el) => {
              if (el && el.complete && el.naturalWidth > 0) {
                // If the image is already in browser cache, it might be complete before onLoad fires
                setImageLoaded(true);
              }
            }}
            onLoad={(event) => {
              setImageLoaded(true);
              if (event.currentTarget.naturalWidth > 0) {
                setImageFailed(false);
              }
            }}
            onError={() => {
              if (imgAttempt < 1) {
                setImgAttempt((n) => n + 1);
                return;
              }
              setImageFailed(true);
            }}
          />
        ) : null}
        {showSpinner ? (
          <div className="ad-creative-thumb__spinner-wrap">
            <div className="ad-creative-thumb__spinner" />
          </div>
        ) : null}
        {showGradientFallback && !useNexusFallback ? (
          <div className={`ad-creative-thumb__placeholder ad-creative-thumb__placeholder--${variant}`} />
        ) : null}
        {playButton}
        {videoBadge}
      </div>
      {modalOpen && inAppPlay ? (
        <AdVideoPlaybackModal ad={ad} onClose={() => setModalOpen(false)} />
      ) : null}
    </>
  );
}
